const HOME_PREVIEW_LIMIT = 4;

export const normalizePosts = (posts) => {
  if (Array.isArray(posts)) return posts;
  if (Array.isArray(posts?.data)) return posts.data;
  return [];
};

const getTimestamp = (value) => {
  const parsed = new Date(value);
  const time = parsed.getTime();
  return Number.isNaN(time) ? null : time;
};

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

const mergeDefinedFields = (base, incoming) => {
  const next = base && typeof base === 'object' ? { ...base } : {};

  if (!incoming || typeof incoming !== 'object') {
    return next;
  }

  Object.entries(incoming).forEach(([key, value]) => {
    if (hasMeaningfulValue(value)) {
      next[key] = value;
    }
  });

  return next;
};

const normalizeCommentUser = (userValue, comment = {}) => {
  if (userValue && typeof userValue === 'object' && !Array.isArray(userValue)) {
    return userValue;
  }

  if (typeof userValue === 'string' && userValue.trim()) {
    return { _id: userValue.trim() };
  }

  if (typeof userValue === 'number' && Number.isFinite(userValue)) {
    return { _id: String(userValue) };
  }

  const fallbackId = String(comment.userId || comment.authorId || comment.memberId || '').trim();
  const firstName = String(comment.firstName || comment.authorFirstName || '').trim();
  const lastName = String(comment.lastName || comment.authorLastName || '').trim();
  const profilePicture = String(comment.profilePicture || comment.authorProfilePicture || '').trim();
  const type = String(comment.type || comment.userType || comment.authorType || '').trim();

  if (!fallbackId && !firstName && !lastName && !profilePicture && !type) {
    return null;
  }

  return {
    ...(fallbackId ? { _id: fallbackId } : {}),
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(profilePicture ? { profilePicture } : {}),
    ...(type ? { type } : {}),
  };
};

export const getCommentIdentity = (comment) => {
  const explicitId = String(comment?._id || comment?.id || '').trim();
  if (explicitId) return explicitId;

  const postId = String(comment?.postId || comment?.post?._id || '').trim();
  const userId = String(comment?.user?._id || comment?.user?.id || comment?.user?.email || '').trim();
  const createdAt = String(comment?.createdAt || '').trim();

  if (postId && userId && createdAt) {
    return `${postId}:${userId}:${createdAt}`;
  }

  const text = String(comment?.comment || '').trim();

  if (postId && createdAt && text) {
    return `${postId}:${createdAt}:${text}`;
  }

  return `${postId || 'post'}:${userId || 'user'}:${createdAt || 'time'}:${text}`;
};

const resolvePostId = (post) => String(post?._id || post?.id || '').trim();

export const buildRecentComments = (recentComments, posts) => {
  const now = Date.now();
  const merged = [];
  const seen = new Map();

  const addComment = (comment, fallback = {}) => {
    if (!comment || typeof comment !== 'object') return;
    const createdAt = comment.createdAt || comment.updatedAt || fallback.createdAt || null;
    const timestamp = getTimestamp(createdAt);
    if (timestamp === null || timestamp > now) return;

    const normalized = {
      ...fallback,
      ...comment,
      createdAt,
      post: comment.post || fallback.post || null,
      postId: comment.postId || comment.post?._id || fallback.postId || fallback.post?._id || null,
      user: normalizeCommentUser(comment.user, comment) || fallback.user || null,
    };

    const identity = getCommentIdentity(normalized);
    const existingIndex = seen.get(identity);

    if (existingIndex !== undefined) {
      const existing = merged[existingIndex];
      merged[existingIndex] = {
        ...mergeDefinedFields(existing, normalized),
        user: mergeDefinedFields(existing?.user, normalized.user),
        post: mergeDefinedFields(existing?.post, normalized.post),
      };
      return;
    }

    seen.set(identity, merged.length);
    merged.push(normalized);
  };

  if (Array.isArray(recentComments)) {
    recentComments.forEach((comment) => addComment(comment));
  }

  normalizePosts(posts).forEach((post) => {
    const comments = Array.isArray(post?.comments) ? post.comments : [];
    comments.forEach((comment) => addComment(comment, {
      post,
      postId: resolvePostId(post) || null,
    }));
  });

  return merged.sort((a, b) => {
    const aTime = getTimestamp(a.createdAt) || 0;
    const bTime = getTimestamp(b.createdAt) || 0;
    return bTime - aTime;
  });
};

export const upsertPost = (posts, incomingPost, { prependIfMissing = false } = {}) => {
  const list = normalizePosts(posts);
  const incomingId = resolvePostId(incomingPost);

  if (!incomingId) {
    return list;
  }

  const index = list.findIndex((post) => resolvePostId(post) === incomingId);

  if (index === -1) {
    return prependIfMissing ? [incomingPost, ...list] : list;
  }

  const next = list.slice();
  next[index] = incomingPost;
  return next;
};

export const buildPreviewFeed = (posts, limit = HOME_PREVIEW_LIMIT) =>
  normalizePosts(posts).slice(0, limit);

export const mergeRecentCommentsWithPost = (recentComments, updatedPost) => {
  if (!updatedPost || typeof updatedPost !== 'object') {
    return Array.isArray(recentComments) ? recentComments : [];
  }

  const updatedPostId = resolvePostId(updatedPost);
  const preservedComments = Array.isArray(recentComments)
    ? recentComments.filter((comment) => {
      const commentPostId = String(comment?.postId || comment?.post?._id || '').trim();
      return !updatedPostId || commentPostId !== updatedPostId;
    })
    : [];

  return buildRecentComments(preservedComments, [updatedPost]);
};

export const ensureMinimumRecentComments = ({
  recentComments,
  posts,
  minimumCount = 0,
  fallbackPosts = [],
  fallbackRecentComments = [],
}) => {
  const nextRecentComments = buildRecentComments(recentComments, posts);

  if (nextRecentComments.length >= minimumCount) {
    return nextRecentComments;
  }

  const withFallbackComments = buildRecentComments([
    ...nextRecentComments,
    ...(Array.isArray(fallbackRecentComments) ? fallbackRecentComments : []),
  ], []);

  if (withFallbackComments.length >= minimumCount) {
    return withFallbackComments;
  }

  return buildRecentComments(withFallbackComments, fallbackPosts);
};

export const syncLiveFeedCollections = ({
  previewFeed,
  recentComments,
  updatedPost,
  prependIfMissing = false,
  previewLimit = HOME_PREVIEW_LIMIT,
}) => {
  const nextPreviewSeed = upsertPost(previewFeed, updatedPost, { prependIfMissing });

  return {
    previewFeed: buildPreviewFeed(nextPreviewSeed, previewLimit),
    recentComments: mergeRecentCommentsWithPost(recentComments, updatedPost),
  };
};

export { HOME_PREVIEW_LIMIT };

export const normalizePostLikeIds = (post) => {
  const likes = Array.isArray(post?.likes) ? post.likes : [];
  return likes.map((entry) => {
    if (typeof entry === 'string' || typeof entry === 'number') return String(entry);
    if (entry && typeof entry === 'object') {
      return String(entry._id || entry.id || entry.user?._id || entry.user?.id || entry.user || '');
    }
    return '';
  }).filter(Boolean);
};

export const buildOptimisticComment = ({ postId, commentText, user }) => {
  const now = new Date().toISOString();
  return {
    _id: `temp-comment:${postId}:${now}`,
    comment: commentText,
    createdAt: now,
    postId,
    user: user ? {
      _id: user._id || user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      profilePicture: user.profilePicture || '',
    } : null,
  };
};

export const applyOptimisticCommentToPost = (post, optimisticComment) => {
  if (!post || !optimisticComment) return post;
  const comments = Array.isArray(post.comments) ? post.comments : [];
  return {
    ...post,
    comments: [...comments, optimisticComment],
  };
};
